import * as React from 'react';
import * as ReactDayPicker from 'react-day-picker';
// tslint:disable:no-submodule-imports
import DayPickerInput from 'react-day-picker/DayPickerInput';
import DayPicker from 'react-day-picker/DayPicker';
import MomentLocaleUtils from 'react-day-picker/moment';
// tslint:enable:no-submodule-imports
import { ThemeContext } from 'styled-components';
import merge from 'lodash/merge';

import { handleEscape } from '../Utils/events/events';

import { DatePickerContainer, DateRangePickerContainer } from './DatePickerStyles';
import { IDatePickerComponent, IDatePickerInputComponent, IDateRangePickerComponent, IDayPickerProps } from './common';
import { dueCell, leadingZero, makeRenderDay, selectedDay } from './renderDay';

const { formatDate, parseDate } = MomentLocaleUtils;

const usePrepDayPickerProps = (props: IDayPickerProps) => {
  /* overrides day picker props from theme with day picker props passed into component
  so the inline props on component instance take precedence */
  const { themeTwo: { datePicker: { dayCell, dayPickerProps: dayPickerPropsFromTheme } } } = React.useContext(ThemeContext);

  const renderDayFns = [selectedDay];

  if (dayCell.leadingZeros) {
    renderDayFns.unshift(leadingZero);
  }

  if (dayCell.due) {
    renderDayFns.push(dueCell);
  }

  if (dayPickerPropsFromTheme && dayPickerPropsFromTheme.renderDay) {
    renderDayFns.push(dayPickerPropsFromTheme.renderDay.bind(dayPickerPropsFromTheme));
  }

  if (props.renderDay) {
    renderDayFns.push(props.renderDay.bind(renderDayFns));
  }

  const p = merge({}, props, { renderDay: makeRenderDay(renderDayFns) });

  return merge({}, dayPickerPropsFromTheme, p);
};

const handleKeyDownInput: (
  e: React.KeyboardEvent<HTMLInputElement>, pickerRef: React.RefObject<ReactDayPicker.default>
) => void = ({ key }, pickerRef) => {
  if (key === 'ArrowDown' && pickerRef.current) {
    pickerRef.current.focus();
  }
};

const handleKeyDownDatePicker = (e: React.KeyboardEvent<HTMLDivElement>, inputRef: React.RefObject<DayPickerInput>) => {
  handleEscape(_ => {
    if (inputRef.current) {
      inputRef.current.hideDayPicker();
      inputRef.current
        .getInput()
        .focus();
    }
  })(e);
};

export const DatePickerInput: IDatePickerInputComponent = props => {
  const inputRef = React.useRef<DayPickerInput>(null);
  const pickerRef = React.useRef<ReactDayPicker.default>(null);

  const { themeTwo: { datePicker: { input: themedInput} } } = React.useContext(ThemeContext);

  const dayPickerProps = usePrepDayPickerProps(props.dayPickerProps || {});

  // inline format prop -> format from theme -> fallback format
  const format = props.format || themedInput.valueFormat || 'YYYY-M-D';
  // placeholder falls back to today's date formatted by format string
  const placeholder = props.placeholder || formatDate(new Date(), format);

  // if disabled/readonly don't show overlay
  const { readOnly, disabled } = (props.inputProps || {}) as { readOnly?: boolean; disabled?: boolean };
  const showOverlay = disabled || readOnly ? false : props.showOverlay;

  return (
    <DatePickerContainer {...props.boxProps} >
      <DayPickerInput
        ref={inputRef}
        {...props}
        showOverlay={showOverlay}
        format={format}
        placeholder={placeholder}
        // date utilities for formatting input values upon day picker selection
        formatDate={formatDate}
        parseDate={parseDate}
        dayPickerProps={{
          // Need to do this because the TS types are incorrect and are not including ref as a prop option
          // @ts-ignore
          ref: pickerRef,
          onKeyDown: e => handleKeyDownDatePicker(e, inputRef),
          ...dayPickerProps,
        }}
        inputProps={{
          onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDownInput(e, pickerRef),
          ...props.inputProps,
        }}
        keepFocus={false}
      />
    </DatePickerContainer>
  );
};

DatePickerInput.displayName = 'DatePickerInput';

export const DatePicker: IDatePickerComponent = props => {
  const dayPickerProps = usePrepDayPickerProps(props || {});

  return (
    <DatePickerContainer {...props.boxProps}>
      <DayPicker
        {...dayPickerProps}
      />
    </DatePickerContainer>
  );
};

DatePicker.displayName = 'DatePicker';
  // tslint:disable-next-line:no-reserved-keywords
export const DateRangePicker: IDateRangePickerComponent = ({ range, ...props }) => {
  const modifiers = {
    start: range.from,
    end: range.to,
  };

  const dayPickerProps = usePrepDayPickerProps(props || {});

  return (
    <DateRangePickerContainer {...props.boxProps}>
      <DayPicker
        {...dayPickerProps}
        // @ts-ignore
        selectedDays={[range.from, range]}
        modifiers={{ ...(props.modifiers || {}), ...modifiers }}
      />
    </DateRangePickerContainer>

  );
};

DateRangePicker.displayName = 'DateRangePicker';

export { ReactDayPicker, DayPickerInput as ReactDayPickerInput };
